import {
  IOfferingRes,
  IQuoteOfferingRespSchema,
  SelectedProductsWithinOffering,
  SelectedProductsWithinOfferingForm,
} from '@monetize/types/app';
import { sortByProductPosition } from '@monetize/utils/core';
import { useEffect, useMemo, useState } from 'react';
import { getAppStore } from '../store/appStore';
import { useGetById } from '../api/queryUtils';
import { ONETIME_CHARGE_PRODUCT_TYPES } from '../constants/quotes';
import { logger } from '../services/logger';
import { activeToastState } from '../store/global.store';
import { arrayToObject } from '../utils/misc';
import { useNonInitialEffect } from './useNonInitialEffect';

interface UseProductSelectionParams {
  offering: IOfferingRes;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  onSave: (data: SelectedProductsWithinOfferingForm) => Promise<void>;
  closeDrawer?: () => void;
  setIsProductListChanged?: (val: boolean) => void;
  /**
   * Onetime charges cannot be added to an offering that carried over from the
   * contract being amended, so those rows are locked here rather than being
   * silently dropped from the payload on save.
   */
  disableOneTimeProducts?: boolean;
}

export const useProductSelection = ({
  offering,
  quoteOffering,
  onSave,
  closeDrawer,
  setIsProductListChanged,
  disableOneTimeProducts,
}: UseProductSelectionParams) => {
  const [searchTerm, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [rootProductValue, setRootProductValue] = useState<
    SelectedProductsWithinOffering[]
  >([]);
  const [visibleRows, setVisibleRows] = useState<
    SelectedProductsWithinOffering[]
  >([]);
  const [isManagedProductsLoading, setIsManagedProductsLoading] =
    useState(false);

  const offeringProduct = useMemo(
    () => offering.offeringProducts.map((item: any) => item.product),
    [offering.offeringProducts],
  );

  const { quoteItemsByProductId } = useMemo(
    () => ({
      quoteItemsByProductId: arrayToObject(offeringProduct, 'id'),
    }),
    [offeringProduct],
  );

  const isAnyProductSelected = useMemo(() => {
    return rootProductValue.some((item) => item.isSelected);
  }, [rootProductValue]);

  const { data: products, isLoading } = useGetById<
    IOfferingRes,
    SelectedProductsWithinOffering[]
  >('productCatalogOfferings', offering.id, {
    refetchOnWindowFocus: false,
    select: (data) =>
      sortByProductPosition(
        data.offeringProducts.map(({ isMandatory, product, position }) => {
          const existingItemsId =
            quoteOffering?.items.map((item) => item.productId) ?? [];

          const hasQuoteItem = !!quoteItemsByProductId[product.id];
          const shouldSelect = existingItemsId.includes(product.id);

          return {
            id: product.id,
            quoteItemId: quoteItemsByProductId[product.id]?.id,
            name: product.name,
            description: product.description,
            productType: product.productType,
            isMandatory,
            isSelected: shouldSelect ? hasQuoteItem : isMandatory,
            position,
          };
        }),
      ),
  });

  useEffect(() => {
    if (products) {
      setRootProductValue(products);
      setVisibleRows(products);
    }
  }, [products]);

  const existingVisibleRow = (id: string) =>
    visibleRows.find((row) => row.id === id);

  const isProductLocked = ({ productType }: SelectedProductsWithinOffering) =>
    !!disableOneTimeProducts && ONETIME_CHARGE_PRODUCT_TYPES.has(productType);

  const resolveSelectAllValue = (
    row: SelectedProductsWithinOffering,
    value: boolean,
  ) => {
    if (row.isMandatory) {
      return true;
    }
    if (isProductLocked(row)) {
      return row.isSelected;
    }
    return value;
  };

  const isAnyChangesMade = useMemo(() => {
    const nonMandatoryProducts = (products ?? []).filter(
      (product) => !product.isMandatory,
    );

    const result = nonMandatoryProducts.some((product) => {
      const rootProduct = rootProductValue.find((row) => row.id === product.id);
      return rootProduct && rootProduct.isSelected !== product.isSelected;
    });

    return result;
  }, [rootProductValue, products]);

  useNonInitialEffect(() => {
    setIsProductListChanged?.(isAnyChangesMade);
  }, [isAnyChangesMade, setIsProductListChanged]);

  useNonInitialEffect(() => {
    setIsSearching(false);
    if (!searchTerm) {
      setVisibleRows(
        rootProductValue.map((product) => {
          const existingRowValue = existingVisibleRow(product.id);
          return {
            ...product,
            isSelected: existingRowValue
              ? existingRowValue.isSelected
              : product.isSelected,
          };
        }),
      );
    } else {
      const searchTermLowercase = searchTerm.toLowerCase();

      const searchProps: (keyof Omit<
        SelectedProductsWithinOffering,
        'isMandatory' | 'isSelected' | 'position'
      >)[] = ['name', 'description', 'productType'];

      setVisibleRows(
        (rootProductValue ?? [])
          .filter((product) => {
            return searchProps.some((prop) =>
              product[prop]?.toLowerCase().includes(searchTermLowercase),
            );
          })
          .map((product) => {
            const existingRowValue = existingVisibleRow(product.id);

            return {
              ...product,
              isSelected: existingRowValue?.isSelected ?? product.isSelected,
            };
          }),
      );
    }
    setIsSearching(true);
  }, [searchTerm, rootProductValue]);

  // Locked rows are excluded so Select All is not permanently indeterminate
  const selectableVisibleRows = visibleRows.filter(
    (row) => !isProductLocked(row),
  );
  const isAllProductSelected =
    selectableVisibleRows.length > 0 &&
    selectableVisibleRows.every(({ isSelected }) => isSelected);
  const isIndeterminate =
    !isAllProductSelected &&
    selectableVisibleRows.some(({ isSelected }) => isSelected);
  const isSelectAllDisabled = selectableVisibleRows.length === 0;

  const selectedProductCount = useMemo(() => {
    return rootProductValue.filter((item) => item.isSelected).length;
  }, [rootProductValue]);

  const handleSelectAll = (value: boolean) => {
    if (selectableVisibleRows.length === 0) {
      return;
    }

    const sharedData = visibleRows.map((row) => ({
      ...row,
      isSelected: resolveSelectAllValue(row, value),
    }));
    setVisibleRows(sharedData);
    setRootProductValue((prevRootProduct) =>
      prevRootProduct.map((row) => ({
        ...row,
        isSelected: resolveSelectAllValue(row, value),
      })),
    );
  };

  const onSubmit = async () => {
    try {
      setIsManagedProductsLoading(true);
      const offeringItems = rootProductValue.filter(
        (item) => item.isSelected || item.isMandatory,
      );

      if (offeringItems.length === 0) {
        getAppStore().set(activeToastState, {
          life: 5000,
          severity: 'error',
          summary: 'Error',
          detail: 'Quote Offering must have at least one quote item.',
        });
        return;
      }

      await onSave({ products: rootProductValue });
      setIsManagedProductsLoading(false);
      setIsProductListChanged?.(false);
      closeDrawer?.();
    } catch (error) {
      logger.error('Error submitting product selection form', error);
      setIsManagedProductsLoading(false);
      setIsProductListChanged?.(false);
    }
  };

  const onError = (error: unknown) => {
    logger.error('Error submitting product selection form', error);
  };

  const handleCheckboxChange = (productId: string, value: boolean) => {
    const updateSelection = (rows: SelectedProductsWithinOffering[]) =>
      rows.map((row) => {
        if (row.id !== productId || isProductLocked(row)) {
          return row;
        }
        return { ...row, isSelected: row.isMandatory ? true : value };
      });

    setVisibleRows((prevVisibleRows) => updateSelection(prevVisibleRows));
    setRootProductValue((prevRootProductValue) =>
      updateSelection(prevRootProductValue),
    );
  };

  const setSearchTerm = (term: string) => {
    setSearch(term);
  };

  return {
    selectedProductCount,
    rootProductValue,
    isAnyProductSelected,
    isAnyChangesMade,
    searchTerm,
    visibleRows,
    isSelectAllDisabled,
    isAllProductSelected,
    isIndeterminate,
    isLoading,
    isSearching,
    isManagedProductsLoading,
    isProductLocked,
    handleCheckboxChange,
    setSearchTerm,
    setVisibleRows,
    handleSelectAll,
    onSubmit,
    onError,
  };
};
